import React from 'react';
import Image from '../../../../../components/presentational/Image';
import { useStyles } from '../../../../../lib/styles';
import { Author, Article } from '../../../../../models/article';
import StoriesBlock from '../../../../../components/presentational/StoriesBlock';
import Typography from '../../../../../components/presentational/Typography';
import StoryAuthor from '../../../../../components/presentational/StoryAuthor';
import Button from '../../../../../components/presentational/Button';
import Input from '../../../../../components/presentational/Input';
import Toast from '../../../../../components/membership/Toast';
import useBehaviour from './HubBlock_behaviour';
import { trackEvent } from '../../../../../lib/ga';
import {
  HUB_FEEDBACK_LINK_CLICKED,
  HUB_SUBSCRIBE_CLICKED,
  HUB_UNSUBSCRIBE_CLICKED,
  HUB_FEEDBACK_GATE_CLICKED,
  HUB_FEEDBACK_SUBMITTED,
} from '../../../lib/ga';

import hubBlockStyles from './HubBlock_styles';
import Callout from '../../../../../components/presentational/Callout';

interface NavigationCard {
  imageURL: string;
  url: string;
  title: string;
}

type HubFeedbackType = 'email' | 'link';

export interface HubBlockData {
  type: 'hub_block';
  title: string;
  subtitle?: string;
  editor: Author;
  blurb: string;
  headerImageURL: string;
  navigationCards?: NavigationCard[];
  subscriptionList: string;
  subscriptionNotice: string;
  articles?: Article[] | any;
  feedbackType: HubFeedbackType;
  feedbackCTA?: string;
  feedbackURL?: string;
  moreArticlesURL?: string;
}

export interface HubBlockProps {
  block: HubBlockData;
  blockIndex: number;
  loadMore: (
    moreArticlesURL: string | undefined,
    i: number,
    articleIds: number[]
  ) => any;
  articleIds: number[];
  moreArticlesLoading: boolean | undefined;
}

const HubBlock = (props: HubBlockProps) => {
  const {
    block: {
      title,
      subtitle,
      navigationCards = [],
      subscriptionList,
      subscriptionNotice,
      editor,
      blurb,
      articles = [],
      feedbackType,
      feedbackCTA = 'Submit a question',
      feedbackURL,
      moreArticlesURL,
    },
    articleIds,
    blockIndex,
    loadMore,
    moreArticlesLoading,
  } = props;
  const styles = useStyles(hubBlockStyles(props));
  const [
    {
      isFeedbackSubmitting,
      isLoading,
      isSaving,
      subscriptions,
      toast,
      feedbackQuestion,
      hasSubmittedFeedback,
      isLoggedIn,
    },
    { setFeedbackQuestion, toggleSubscription, clearToast, submitFeedback },
  ] = useBehaviour();

  const isSubscribed =
    subscriptions &&
    subscriptions.subscriptions[subscriptionList] &&
    !subscriptions.unsubscribed;

  return (
    <div className={styles.wrapper}>
      <div className={styles.topPanel}>
        <div className={styles.topContainer}>
          <Typography align={'center'} color={'white'} className={styles.scrim}>
            <h1>{title}</h1>
            {subtitle && <h4>{subtitle}</h4>}
          </Typography>

          <Button
            color={isSubscribed ? 'white' : 'primary'}
            className={styles.subscribe}
            onClick={() => {
              if (isSaving) return;

              toggleSubscription(subscriptionList);
              trackEvent(
                isSubscribed ? HUB_UNSUBSCRIBE_CLICKED : HUB_SUBSCRIBE_CLICKED
              );
            }}
            disabled={isLoading || isSaving}
          >
            {isLoading || isSaving ? (
              <div className={styles.loading} />
            ) : isSubscribed ? (
              'Unsubscribe'
            ) : (
              'Subscribe'
            )}
          </Button>
        </div>
      </div>

      <div className={styles.middlePanel}>
        <div className={styles.middleContainer}>
          <div className={styles.introduction}>
            <Typography align={'left'}>
              <small>{subscriptionNotice}</small>
            </Typography>

            <Typography
              align={'left'}
              dangerouslySetInnerHTML={{ __html: blurb }}
            />

            <StoryAuthor
              author={{ ...editor, title: editor.title || 'Edited by' }}
            />
          </div>

          <Callout title={feedbackCTA} className={styles.feedbackCallout}>
            {feedbackType === 'link' && (
              <>
                <Typography>Learn how to contribute to this hub.</Typography>

                <Button
                  href={feedbackURL}
                  onClick={() =>
                    trackEvent(HUB_FEEDBACK_LINK_CLICKED, {
                      transport: 'beacon',
                    })
                  }
                >
                  Learn more
                </Button>
              </>
            )}

            {feedbackType === 'email' && (
              <>
                {!hasSubmittedFeedback && isLoggedIn && (
                  <form
                    className={styles.feedbackForm}
                    onSubmit={event => {
                      event.preventDefault();

                      if (!feedbackQuestion) return false;

                      submitFeedback(subscriptionList, feedbackQuestion);
                      trackEvent(HUB_FEEDBACK_SUBMITTED);

                      return false;
                    }}
                  >
                    <Input
                      type={'textarea'}
                      value={feedbackQuestion}
                      onChange={question => setFeedbackQuestion(question)}
                      placeholder={`What's your question?`}
                    />

                    <Button
                      type={'submit'}
                      disabled={!feedbackQuestion || isFeedbackSubmitting}
                    >
                      {isFeedbackSubmitting ? (
                        <div className={styles.loading} />
                      ) : (
                        'Submit'
                      )}
                    </Button>
                  </form>
                )}

                {hasSubmittedFeedback && isLoggedIn && (
                  <Typography>
                    <p>
                      Thanks! Your question has been submitted to the editor.
                    </p>
                  </Typography>
                )}

                {!isLoggedIn && (
                  <div className={styles.feedbackGate}>
                    <Typography>
                      <p>
                        Starts at 60 members can submit questions to the editor
                        - along with some other pretty amazing benefits!
                      </p>
                    </Typography>

                    <Button
                      href={'/membership'}
                      onClick={() =>
                        trackEvent(HUB_FEEDBACK_GATE_CLICKED, {
                          transport: 'beacon',
                        })
                      }
                    >
                      Join now, it's free
                    </Button>
                  </div>
                )}
              </>
            )}
          </Callout>
        </div>
      </div>

      {navigationCards.length > 0 && articles.length > 0 && (
        <div className={styles.bottomPanel}>
          <div className={styles.bottomContainer}>
            {navigationCards.length > 0 && (
              <div className={styles.navigationCards}>
                {navigationCards.map(({ url, imageURL, title }) => (
                  <a key={title} href={url} className={styles.navigationCard}>
                    <Image
                      src={imageURL}
                      alt={title}
                      width={50}
                      height={50}
                      resizeWidth={50}
                      aspect={[1, 1]}
                    />

                    <div className={styles.navigationCardTitle}>{title}</div>
                  </a>
                ))}
              </div>
            )}

            {articles.length > 0 && (
              <StoriesBlock
                articles={articles}
                attribute={false}
                isLoading={!!moreArticlesLoading}
                hasMore={!!moreArticlesURL}
                hasMoreColor={'primary'}
                onShowMore={() =>
                  loadMore(moreArticlesURL, blockIndex, articleIds)
                }
              />
            )}
          </div>
        </div>
      )}

      {toast && (
        <Toast
          action={{
            text: 'Close',
            onClick: () => clearToast(),
          }}
        >
          {toast}
        </Toast>
      )}
    </div>
  );
};

export default HubBlock;
